// USADO PARA HACER GET | LIST | DETAIL (CON URL Y PAGINADO OPCIONAL)
import { API_CORE } from "../../../../API/ApiCORE";
import { useSearchParams } from "react-router-dom";
import { useState, useCallback, useEffect } from "react";
import { useUserAuthenticated } from "../../Contexts/_Auth/userAuthenticatedContext";
import { REACT_HOT_TOAST_ERROR } from "../../../../HELPERS/reactHotToast.helper";
import { isErrorStatusResponse, isSuccessStatusResponse, isWarningStatusResponse } from "../../../../API/_ApiUtil/statusResponseForMessages";

const DEFAULT_LOGOUT_ON_401 = String(process.env.REACT_APP_AUTH_LOGOUT_ON_401 ?? "false").trim().toLowerCase() === "true";

const onErrorText = (message, detail, requestId) => [
    message,
    detail && detail !== message ? detail : null,
    requestId ? `Referencia: ${requestId}` : null,
].filter(Boolean).join("\n");

export const useApiQuery = ({
    minTimeQuery = 1500, isToastError = true, isForm,
    syncWithUrl = false, enablePagination = true, defaultParams = {},
    startLoading = false, logoutOn401 = DEFAULT_LOGOUT_ON_401,
} = {}) => {

    const { onCallbackCloseSessionUserAuth } = useUserAuthenticated();

    const [useApiQueryData, setApiQueryData] = useState(null);
    const [useApiQueryMeta, setApiQueryMeta] = useState(null);
    const [useApiQueryIsLoading, setApiQueryIsLoading] = useState(startLoading);
    const [useApiQueryError, setApiQueryError] = useState(null);

    const [searchParams, setSearchParams] = useSearchParams();

    // -------------------------------------------------- 🔥 PARAMS STATE
    const [useQueryParams, setQueryParams] = useState(() => {
        if (!syncWithUrl) return defaultParams;

        const paramsFromUrl = {};
        searchParams.forEach((value, key) => {
            paramsFromUrl[key] = value;
        });

        return { ...defaultParams, ...paramsFromUrl };
    });

    // -------------------------------------------------- 🔥 URL SYNC
    useEffect(() => {
        if (!syncWithUrl) return;

        const paramsToUrl = {};
        Object.entries(useQueryParams).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
                paramsToUrl[key] = value;
            }
        });

        setSearchParams(paramsToUrl, { replace: true });
    }, [useQueryParams, syncWithUrl, setSearchParams]);

    // -------------------------------------------------- 🔥 GET HANDLER
    const CB__useApiGet = useCallback(async ({ endpoint, isDetail = false, config = {} }) => {

        const start_query = Date.now();
        setApiQueryIsLoading(true);
        setApiQueryError(null);

        const keepMinTime = async () => {
            const elapsed = Date.now() - start_query;
            if (elapsed < minTimeQuery) {
                await new Promise(resolve => setTimeout(resolve, minTimeQuery - elapsed));
            }
        };

        /*
            -------------------- PARAMS FINALES --------------------

            ⚠️ Los params que llegan en la llamada mandan sobre los del estado.

            setApiParams guarda en estado y el estado se aplica en el render
            SIGUIENTE. Si una vista hace setApiParams y en el mismo ciclo
            dispara la busqueda, la peticion sale con los filtros VIEJOS: se
            filtra por lo anterior y al limpiar parece que no hay datos.

            Pasando los filtros en config.params la peticion siempre sale con
            lo que el usuario acaba de elegir.
        */
        const FINAL_PARAMS = {};
        Object.entries({ ...useQueryParams, ...(config.params ?? {}) }).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
                FINAL_PARAMS[key] = value;
            }
        });

        if (!enablePagination) {
            delete FINAL_PARAMS.page;
            delete FINAL_PARAMS.limit;
        }

        // -------------------- RESOLVER ENDPOINT (API_CORE) --------------------
        let urlResource = API_CORE.ENDPOINTS[endpoint];

        if (!urlResource) {
            await keepMinTime();
            setApiQueryIsLoading(false);
            return { code_status: 400, success: false, data: null, meta: null, message: `Invalid endpoint: ${endpoint}` };
        }

        // Reemplazo de path params (ej: /users/:id  ->  /users/123)
        if (config.pathParams) {
            Object.entries(config.pathParams).forEach(([key, value]) => {
                urlResource = urlResource.replace(`:${key}`, value);
            });
            delete config.pathParams;
        }

        let RESPONSE;
        try {
            if (isDetail) {
                RESPONSE = await API_CORE.SERVICES.GET_RESOURCE(urlResource, config);
            } else {
                RESPONSE = await API_CORE.SERVICES.GET_RESOURCES(urlResource, FINAL_PARAMS, config);
            }
        } catch (error) {
            await keepMinTime();
            setApiQueryIsLoading(false);
            const ERR_STATUS = error?.response?.status || 500;
            const ERR_MESSAGE = error?.response?.data?.message || error?.message || "Error de red";
            const ERR_DETAIL = error?.response?.data?.detail || null;
            const REQUEST_ID = error?.response?.data?.requestId || null;
            setApiQueryError({ code_status: ERR_STATUS, message: ERR_MESSAGE, detail: ERR_DETAIL, requestId: REQUEST_ID, isOffline: !error?.response });
            if (isToastError) REACT_HOT_TOAST_ERROR(onErrorText(ERR_MESSAGE, ERR_DETAIL, REQUEST_ID), { duration: 7000 });
            if (ERR_STATUS === 401 && logoutOn401) onCallbackCloseSessionUserAuth();
            return { code_status: ERR_STATUS, success: false, data: null, meta: null, message: ERR_MESSAGE, detail: ERR_DETAIL, requestId: REQUEST_ID };
        }

        await keepMinTime();
        setApiQueryIsLoading(false);

        const CODE_STATUS = RESPONSE?.status ?? 200;
        const DATA = RESPONSE?.data?.data ?? RESPONSE?.data ?? null;
        const META = RESPONSE?.meta || RESPONSE?.data?.meta || null;
        const RESPONE_MESSAGE = RESPONSE?.message || RESPONSE?.msg || RESPONSE?.data?.message || null;
        const RESPONSE_DETAIL = RESPONSE?.detail || RESPONSE?.data?.detail || null;
        const REQUEST_ID = RESPONSE?.requestId || RESPONSE?.data?.requestId || null;

        const IS_ERROR_SERVER = isErrorStatusResponse(CODE_STATUS);
        const IS_ERROR_CLIENT = isWarningStatusResponse(CODE_STATUS);
        const IS_SUCCESS_STATUS = isSuccessStatusResponse(CODE_STATUS);

        // ----------------- ERROR 5XX (Servidor) -----------------
        if (IS_ERROR_SERVER) {
            setApiQueryError({ code_status: CODE_STATUS, message: RESPONE_MESSAGE, detail: RESPONSE_DETAIL, requestId: REQUEST_ID, isOffline: CODE_STATUS === 503 });
            if (isToastError && RESPONE_MESSAGE) REACT_HOT_TOAST_ERROR(onErrorText(RESPONE_MESSAGE, RESPONSE_DETAIL, REQUEST_ID), { duration: 7000 });
            return { code_status: CODE_STATUS, success: false, data: null, meta: null, message: RESPONE_MESSAGE, detail: RESPONSE_DETAIL, requestId: REQUEST_ID };
        }

        // ----------------- ERROR 4XX (Cliente) -----------------
        if (IS_ERROR_CLIENT) {
            if (CODE_STATUS === 401 && logoutOn401) onCallbackCloseSessionUserAuth();
            setApiQueryError({ code_status: CODE_STATUS, message: RESPONE_MESSAGE, detail: RESPONSE_DETAIL, requestId: REQUEST_ID, isOffline: false });
            if (!(CODE_STATUS === 404 && !isDetail)) {
                if (isToastError && RESPONE_MESSAGE) REACT_HOT_TOAST_ERROR(onErrorText(RESPONE_MESSAGE, RESPONSE_DETAIL, REQUEST_ID), { duration: 7000 });
            }
            return { code_status: CODE_STATUS, success: false, data: null, meta: null, message: RESPONE_MESSAGE, detail: RESPONSE_DETAIL, requestId: REQUEST_ID };
        }

        // ----------------- SUCCESS 2XX -----------------
        if (IS_SUCCESS_STATUS) {
            setApiQueryData(DATA);
            setApiQueryMeta(META);
            return { code_status: CODE_STATUS, success: true, data: DATA, meta: META, message: RESPONE_MESSAGE };
        }

        // ----------------- FALLBACK -----------------
        return { code_status: 500, success: false, data: null, meta: null, message: "Unexpected response" };

    }, [useQueryParams, minTimeQuery, isToastError, enablePagination, logoutOn401, onCallbackCloseSessionUserAuth]);

    // -------------------------------------------------- 🔥 MUTATE LOCAL (IDEMPOTENTE)
    /*
        Actualiza el state local sin pegarle al backend.
        Útil después de un mutation OK o al recibir un evento socket.

        IDEMPOTENTE:
        - insert: si el id ya existe en el listado, hace PATCH en vez de duplicar.
        - remove: si el id no está, no rompe nada.
        - patch: si el id no está, no rompe nada.

        Uso:
            mutateLocal({ id: "abc", patch: { status: "ARCHIVED" } })
            mutateLocal({ id: "abc", remove: true })
            mutateLocal({ insert: newRow })
            mutateLocal({ insert: newRow, position: "end" })
            mutateLocal({ replaceAll: [...] })
    */
    const CB__useMutateLocal = useCallback(({ id, patch, remove, insert, position = "start", replaceAll }) => {

        // Reemplazo total
        if (replaceAll) {
            setApiQueryData(replaceAll);
            return;
        }

        // Insertar nueva fila (idempotente: si ya existe → patch)
        if (insert) {
            const newId = insert.id;
            setApiQueryData((prev) => {
                const list = Array.isArray(prev) ? prev : [];
                const alreadyExists = newId && list.some((r) => r.id === newId);

                if (alreadyExists) {
                    /* en vez de duplicar, mergeamos los campos */
                    return list.map((row) => row.id === newId ? { ...row, ...insert } : row);
                }

                return position === "end" ? [...list, insert] : [insert, ...list];
            });

            setApiQueryMeta((prev) => {
                if (!prev) return prev;
                const currentList = Array.isArray(useApiQueryData) ? useApiQueryData : [];
                const wasNew = !newId || !currentList.some((r) => r.id === newId);
                if (!wasNew) return prev;

                const currentTotal = prev.pagination?.totalItems ?? prev.total ?? 0;
                const newTotal = currentTotal + 1;
                const limit = prev.pagination?.pageSize ?? prev.limit ?? 1;
                const newTotalPages = Math.max(1, Math.ceil(newTotal / limit));

                return {
                    ...prev,  total: newTotal,   totalPages: newTotalPages,
                    pagination: prev.pagination ? {
                        ...prev.pagination,  totalItems: newTotal, totalPages: newTotalPages,
                    } : prev.pagination,
                };
            });
            return;
        }

        // Eliminar fila
        if (remove && id) {
            let wasRemoved = false;
            setApiQueryData((prev) => {
                if (!Array.isArray(prev)) return prev;
                const filtered = prev.filter((row) => row.id !== id);
                wasRemoved = filtered.length !== prev.length;
                return filtered;
            });

            setApiQueryMeta((prev) => {
                if (!prev || !wasRemoved) return prev;

                /* El meta real del backend está en prev.pagination, pero también puede
                   estar en prev.total (depende del normalize). Actualizamos ambos lados. */
                const currentTotal = prev.pagination?.totalItems ?? prev.total ?? 0;
                const newTotal = Math.max(0, currentTotal - 1);
                const limit = prev.pagination?.pageSize ?? prev.limit ?? 1;
                const newTotalPages = Math.max(1, Math.ceil(newTotal / limit));

                return {
                    ...prev,  total: newTotal,  totalPages: newTotalPages,
                    pagination: prev.pagination ? {
                        ...prev.pagination,  totalItems: newTotal,  totalPages: newTotalPages,
                    } : prev.pagination,
                };
            });
            return;
        }

        if (id && patch) {
            setApiQueryData((prev) => {
                if (Array.isArray(prev)) {
                    return prev.map((row) => row.id === id ? { ...row, ...patch } : row);
                }

                // Detail views also live in useApiQuery. Patch them locally instead
                // of forcing a second GET after a successful mutation.
                if (prev && typeof prev === "object" && String(prev.id ?? "") === String(id)) {
                    return { ...prev, ...patch };
                }

                return prev;
            });
        }
    }, [useApiQueryData]);

    return {
        apiGet: CB__useApiGet,
        mutateLocal: CB__useMutateLocal,
        useApiLoading: useApiQueryIsLoading,
        useApiError: useApiQueryError,
        useApiData: useApiQueryData, useApiMeta: useApiQueryMeta,
        useApiParams: useQueryParams, setApiParams: setQueryParams,
    };
};